import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { LntDrawer } from '@app/components/base'
import { ExtendedTable, TableSettingsToolbar } from '@app/components'

import { usePaginationParams } from '@app/hooks'

import { getExternalProductsSearchListFromResponse } from '@app/utils/responseAdapters'
import { objectHasNonNullableProp } from '@app/utils/object'

import {
  getExternalProductsSearchProductsList,
  getExternalProductsSearchList
} from '@app/api/routes/externalProductsCatalog'

import { EXTERNAL_PRODUCTS_CATALOG_KEY } from '@app/constants/queryKeys'

import { getColumns } from './utils'

import { TopFilters } from './TopFilters'
import { CommonExternalSearchCatalogFooter } from './CommonExternalSearchCatalogFooter'

import styles from './styles.module.scss'

import type { AddProductFiltersData, CommonCatalogDrawerProps } from './types'
import type { GetCommonProductsFilterParams } from '@app/api/routes/externalProductsCatalog/types'
import type { ExternalSearchProductsListDataSource } from '@app/types/dataSource'
import type { Column } from '@app/components/base/Table/types'

const toolbar = <TableSettingsToolbar key='table-settings-toolbar' />

const columns = getColumns()

export const CommonExternalSearchCatalogDrawer: React.FC<CommonCatalogDrawerProps> = props => {
  const { onClose, isSearchProducts, isConfirmed, onAddProduct, ...drawerProps } = props
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationParams()

  const [dataFilters, setDataFilters] = useState<AddProductFiltersData>({})
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const rowSelectionType = useMemo(
    () => (isSearchProducts ?? isConfirmed ? 'radio' : 'checkbox'),
    [isSearchProducts, isConfirmed]
  )

  const hasFiltersData = useMemo(() => objectHasNonNullableProp(dataFilters), [dataFilters])
  const dataListParams = useMemo(() => ({ ...paginationParams, ...dataFilters }), [paginationParams, dataFilters])

  const {
    data: searchListData,
    isLoading: isLoadingSearchListData,
    remove: removeSearchListData
  } = useQuery({
    enabled: !!isSearchProducts && hasFiltersData,
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalSearchLentaProductsList, dataListParams],
    queryFn: async () => await getExternalProductsSearchProductsList(dataListParams)
  })

  const {
    data: listData,
    isLoading: isLoadingListData,
    remove: removeListData
  } = useQuery({
    enabled: !isSearchProducts && hasFiltersData,
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalSearchProductsList, dataListParams],
    queryFn: async () => await getExternalProductsSearchList({ ...dataListParams, isConfirmed })
  })

  const isLoadingData = useMemo(() => {
    const isLentaProductsSearch = !!isSearchProducts && hasFiltersData

    return isLentaProductsSearch ? isLoadingSearchListData : isLoadingListData
  }, [isLoadingSearchListData, isLoadingListData])

  const dataSource = useMemo(
    () => getExternalProductsSearchListFromResponse(searchListData ?? listData),
    [searchListData, listData]
  )

  const handleSubmitFilter = (data: GetCommonProductsFilterParams): void => {
    setDataFilters(data)
    resetCurrentPage()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: React.Key[]): void => {
    setSelectedIds(selectedKeys.map(String))
  }

  const handlePostLists = (): void => {
    if (!selectedIds.length) return

    onAddProduct?.(selectedIds)
    onClose?.()
  }

  const handleClose = (): void => {
    setDataFilters({})
    removeSearchListData()
    removeListData()
    onClose()
  }

  return (
    <LntDrawer
      { ...drawerProps }
      width={ 1080 }
      onClose={ handleClose }
      footer={
        <CommonExternalSearchCatalogFooter
          disabled={ !selectedIds.length }
          onAddProduct={ handlePostLists }
        />
      }
    >
      <div className={ styles.commonCatalog__container }>
        <TopFilters onSearch={ handleSubmitFilter } />

        <ExtendedTable<ExternalSearchProductsListDataSource>
          loading={ hasFiltersData && isLoadingData }
          toolbar={ toolbar }
          columns={ columns as Array<Column<ExternalSearchProductsListDataSource>> }
          dataSource={ dataSource }
          rowSelection={ {
            type: rowSelectionType,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: searchListData?.count,
            onChange: handlePaginationChange
          } }
        />
      </div>
    </LntDrawer>
  )
}
